import { z } from 'zod';
import type { Prettify } from '@pdcfrontendui/types';

export type DecodedSearchParamsSchema<T extends z.ZodObject<z.ZodRawShape>> = {
  [P in keyof T['shape'] as T['shape'][P] extends z.ZodCatch<z.ZodTypeAny>
    ? P
    : never]: z.infer<T['shape'][P]>;
} & {
  [P in keyof T['shape'] as T['shape'][P] extends z.ZodCatch<z.ZodTypeAny>
    ? never
    : P]?: z.infer<z.ZodOptional<T['shape'][P]>>;
};

/**
 * Converts a url string into a query object with type validation.
 * Validation is necessary because URLs can be changed by the user to any value, and since we might change params, enum values, etc.
 * Pro tip: Using .catch(defaultValue) on schema entries allows providing default values for invalid or missing parameters.
 */
export function decodeSearchParams<
  T extends z.ZodObject<Record<string, z.ZodType>>,
>(
  schema: T,
  location: { search: string }
): Prettify<DecodedSearchParamsSchema<T>> {
  const unparsedParams = new URLSearchParams(location.search);
  const parsedParams = {} as DecodedSearchParamsSchema<T>;
  for (const key in schema.shape) {
    const result = schema.shape[key]?.safeParse(unparsedParams.get(key));
    if (result?.success) {
      // @ts-expect-error TypeScript cannot infer that the types match here
      parsedParams[key as keyof DecodedSearchParamsSchema<T>] = result.data;
    }
  }
  return parsedParams;
}
